import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AppRequirement,
  AppResponse,
  MarketplaceAppService as AppService,
  AppVendor,
  ApprovalProcess,
  ApproveAppRequest,
  BillingProcess,
  CreateAppRequest,
  DeleteAppRequest,
  DistributionSetting,
  EndOfTrialActions,
  FieldMask,
  MarketingInformation,
  MarketplaceApp,
  MarketplaceAppsApiService,
  OrderForm,
  PublishAppRequest,
  RequestAppReviewRequest,
  Restrictions,
  Rule,
  SuspendAppRequest,
  UnapproveAppRequest,
  UnsuspendAppRequest,
  UpdateAppApprovalAndBillingSettingsRequest,
  UpdateAppGeneralInformationRequest,
  UpdateAppIntegrationRequest,
  UpdateAppMarketingInformationRequest,
  UpdateAppOrderFormRequest,
  UpdateAppSSOIntegrationRequest,
  UpdateAppTrialConfigurationRequest,
  VendorService,
} from '@galaxy/marketplace-apps/v1';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FulfillmentOrderConfig, FulfillmentOrderConfigApiService, ReleaseType } from '@vendasta/marketplace-apps';
import { AreAppsDistributedToPartnerResponse, MarketplacePackagesApiService } from '@vendasta/marketplace-packages';
import { retryer } from '@vendasta/rx-utils';
import { BehaviorSubject, Observable, combineLatest as observableCombineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, skipWhile, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { MarketplaceAppDiffService } from '../marketplace-app-diff/marketplace-app-diff.service';

@Injectable()
export class MarketplaceAppService {
  constructor(
    private authService: AuthService,
    private apiService: MarketplaceAppsApiService,
    private marketplacePackagesApiService: MarketplacePackagesApiService,
    private appService: AppService,
    private vendorService: VendorService,
    private marketplaceAppDiffService: MarketplaceAppDiffService,
    private snackbarService: SnackbarService,
    private fufillmentOrderConfigApiService: FulfillmentOrderConfigApiService,
  ) {
    this.currentAppId$
      .pipe(
        switchMap((appId) => {
          return this.getDraft(appId)
            .pipe(this.errorStrategy$)
            .pipe(
              catchError(() => {
                this.snackbarService.openErrorSnack('Unable to load app. Please try again.');
                return of(null);
              }),
            );
        }),
      )
      .subscribe((app: MarketplaceApp) => {
        this.currentDraft$$.next(app);
      });

    this.currentAppId$
      .pipe(
        filter((appId) => !!appId),
        switchMap((appId) => {
          return this.marketplacePackagesApiService.areAppsDistributedToPartner({ appIds: [appId] }).pipe(
            catchError(() => {
              this.snackbarService.openErrorSnack('Unable to load all app details. Please try again.');
              return of(null);
            }),
          );
        }),
      )
      .subscribe((resp: AreAppsDistributedToPartnerResponse) => {
        if (resp && !!resp.areAppsDistributed && resp.areAppsDistributed.length > 0) {
          const isDistributed =
            resp.areAppsDistributed[0]?.isDistributed && !resp.areAppsDistributed[0].isSolelyDistributedToPartner;
          this.currentAppIsDistributed$$.next(isDistributed);
        }
      });

    this.currentPublished$ = this.currentApp$.pipe(
      filter((app) => !!app),
      switchMap((app) =>
        this.getPublished(app.appId).pipe(
          this.errorStrategy$,
          catchError((err: HttpErrorResponse) => {
            if (err.status === 404) {
              return of({});
            }
          }),
        ),
      ),
      shareReplay(1),
    ) as Observable<MarketplaceApp>;

    this.currentAppHasUnpublishedChanges$ = observableCombineLatest(this.currentApp$, this.currentPublished$).pipe(
      map(([draft, published]) => {
        const diffModel = this.marketplaceAppDiffService.getMarketPlaceAppDiffs(draft, published);
        return diffModel.hasChanges();
      }),
    );

    this.currentAppId$
      .pipe(switchMap((currentAppId) => this.getFulfillmentOrderConfig(currentAppId)))
      .subscribe((foConfig) => this.fulfillmentOrderConfig$$.next(foConfig));
  }

  get currentAppId$(): Observable<string> {
    return this.currentAppId$$.asObservable().pipe(
      distinctUntilChanged(),
      filter((id) => !!id),
    );
  }

  // The current published app
  get currentPublishedApp(): Observable<MarketplaceApp> {
    return this.currentPublished$;
  }

  // The Current App in its draft form.
  get currentApp$(): Observable<MarketplaceApp> {
    return this.currentDraft$$.asObservable().pipe(skipWhile((a) => !a));
  }

  get currentApp(): MarketplaceApp {
    return this.currentDraft$$.getValue();
  }

  set currentApp(app: MarketplaceApp) {
    this.currentDraft$$.next(app);
  }

  // The vendor of the current draft app.
  get currentAppVendor$(): Observable<AppVendor> {
    return this.currentApp$.pipe(
      switchMap((app) => this.vendorService.getMulti([app.vendorId])),
      map((resp) => (resp?.length > 0 ? resp[0] : null)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  // The partner ID of the vendor of the current draft app.
  get currentAppVendorPartnerId$(): Observable<string> {
    return this.currentAppVendor$.pipe(
      map((vendor) => vendor.partnerId),
      filter((partnerId) => !!partnerId),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  get currentAppIsDistributed$(): Observable<boolean> {
    return this.currentAppIsDistributed$$.asObservable().pipe(
      skipWhile((x) => x === null),
      shareReplay(1),
    );
  }
  currentAppId$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private currentDraft$$: BehaviorSubject<MarketplaceApp> = new BehaviorSubject<MarketplaceApp>(null);
  private currentPublished$: Observable<MarketplaceApp>;
  private currentAppHasUnpublishedChanges$: Observable<boolean>;
  private currentAppIsDistributed$$: BehaviorSubject<boolean> = new BehaviorSubject(null);

  private fulfillmentOrderConfig$$: BehaviorSubject<FulfillmentOrderConfig> = new BehaviorSubject(null);
  public readonly fulfillmentOrderConfig$: Observable<FulfillmentOrderConfig> =
    this.fulfillmentOrderConfig$$.asObservable();

  private errorStrategy$: any = retryer({
    maxAttempts: 3,
    retryDelay: 200,
    timeoutMilliseconds: 10000,
    customRetryChecker: (errResp: HttpErrorResponse) => errResp.status === 503 || errResp.status === 504,
  });

  static createUpdateAppMarketingInformationRequest(
    appId: string,
    marketingInfo: MarketingInformation,
    usesMarketingInfo?: boolean,
  ): UpdateAppMarketingInformationRequest {
    const request = new UpdateAppMarketingInformationRequest();
    request.appId = appId;
    if (marketingInfo) {
      request.marketingInfo = marketingInfo;
      request.usesMarketingInformation = usesMarketingInfo;
    }
    return request;
  }

  static createUpdateAppGeneralSettingsRequest(
    app: MarketplaceApp,
    suggestedRetailSetupFee: number,
    fm: FieldMask,
  ): UpdateAppGeneralInformationRequest {
    const request = new UpdateAppGeneralInformationRequest({ fieldMask: fm });
    request.appId = app.appId;
    request.allowPriceNegotiation = app.allowPriceNegotiation;
    request.billingFrequency = app.billingFrequency;
    request.currency = app.currency;
    request.headerImage = app.headerImage;
    request.icon = app.icon;
    request.lmiCategories = app.lmiCategories;
    request.categories = app.categories;
    request.serviceModel = app.serviceModel;
    request.name = app.name;
    request.deliveryMethod = app.deliveryMethod;
    request.price = app.price;
    request.cost = app.cost;
    request.fees = app['Fees'];
    request.setupFee = app.setupFee;
    request.suggestedRetailSetupFee = suggestedRetailSetupFee;
    request.usesCustomPricing = app.usesCustomPricing;
    request.screenshot = app.screenshot;
    request.tagline = app.tagline;
    if (app.restrictions) {
      request.country = app.restrictions.country;
    }
    request.preventPriceConversionBetweenCurrencies = app.preventPriceConversionBetweenCurrencies;
    request.suggestedRetailPrice = app.suggestedRetailPrice;
    request.commitment = app.commitment;
    request.multipleActivations = app.multipleActivations;
    request.discoverable = app.discoverable;
    request.restrictions = app.restrictions;
    request.editionIds = app.editionIds;
    request.state = app.state;
    request.usesEditions = app.usesEditions;
    return request;
  }

  static createUpdateAppSSOUrlsRequest(app: MarketplaceApp): UpdateAppSSOIntegrationRequest {
    const request = new UpdateAppSSOIntegrationRequest();
    request.appId = app.appId;
    if (app.integration) {
      request.entryUrl = app.integration.entryUrl;
      request.logoutUrl = app.integration.logoutWebhookUrl;
      request.sessionUrl = app.integration.sessionUrl;
    }
    request.usesSsoIntegration = app.usesSsoIntegration;
    return request;
  }

  static createUpdateAppIntegrationRequest(app: MarketplaceApp): UpdateAppIntegrationRequest {
    const request = new UpdateAppIntegrationRequest();
    request.appId = app.appId;
    if (app.integration) {
      request.accountModWebhookUrl = app.integration.accountModWebhookUrl;
      request.activationRequiresApproval = app.integration.activationRequiresApproval;
      request.addonPurchaseWebhookUrl = app.integration.addonPurchaseWebhookUrl;
      request.contactWebhookUrl = app.integration.contactWebhookUrl;
      request.cancellationWebhookUrl = app.integration.cancellationWehookUrl;
      request.fulfillmentEmail = app.integration.fulfillmentEmail;
      request.partnerContactRecipients = app.integration.partnerContactRecipients;
      request.publicKey = app.integration.publicKey;
      request.purchaseWebhookUrl = app.integration.purchaseWebhookUrl;
      request.userModWebhookUrl = app.integration.userModWebhookUrl;
      request.partnerSettingsWebhookUrl = app.integration.partnerSettingsWebhookUrl;
      request.allowWhiteLabeling = app.integration.allowWhiteLabeling;
      request.sendCancelationNotifications = app.integration.sendCancelationNotifications;
      request.serviceChangeWebhookUrl = app.integration.serviceChangeWebhookUrl;
      request.appLevelHasAdminDashboard = app.integration.appLevelHasAdminDashboard;
    }
    request.usesIntegration = app.usesIntegration;
    return request;
  }

  static createUpdateAppOrderFormRequest(
    appId: string,
    usesOrderForm: boolean,
    usesActivationSpecificEntryUrl: boolean,
    orderForm: OrderForm,
    applicationUrl: string,
  ): UpdateAppOrderFormRequest {
    const request = new UpdateAppOrderFormRequest();
    request.appId = appId;
    request.usesOrderForm = usesOrderForm;
    request.usesActivationSpecificEntryUrl = usesActivationSpecificEntryUrl;
    request.applicationUrl = applicationUrl;
    if (orderForm) {
      request.orderForm = orderForm;
    }
    return request;
  }

  public clearApp(): void {
    this.currentDraft$$.next(null);
    this.currentAppId$$.next(null);
    this.currentAppIsDistributed$$.next(null);
  }

  public publishApp(appId: string): Observable<MarketplaceApp> {
    const request = new PublishAppRequest();
    request.appId = appId;
    return this.apiService.publishApp(request).pipe(map((resp: AppResponse) => resp.app));
  }

  public requestAppReview(appId: string): Observable<HttpResponse<null>> {
    const request = new RequestAppReviewRequest();
    request.appId = appId;
    return this.apiService.requestAppReview(request);
  }

  getCurrentAppId(): string {
    return this.currentAppId$$.getValue();
  }

  setCurrentAppId(appId: string): void {
    this.currentAppId$$.next(appId);
  }

  getPublished(appId: string): Observable<MarketplaceApp> {
    return this.appService.getApp(appId);
  }

  getDraft(appId: string): Observable<MarketplaceApp> {
    return this.appService.getAppDraft(appId);
  }

  public approve(appId: string): Observable<MarketplaceApp> {
    const request = new ApproveAppRequest();
    request.appId = appId;

    return this.apiService.approveApp(request).pipe(map((resp) => resp.app));
  }

  public unapprove(appId: string): Observable<MarketplaceApp> {
    const request = new UnapproveAppRequest();
    request.appId = appId;

    return this.apiService.unapproveApp(request).pipe(map((resp) => resp.app));
  }

  public openAppToMarketplace(appId: string): Observable<MarketplaceApp> {
    const request = new UpdateAppApprovalAndBillingSettingsRequest();
    request.appId = appId;
    request.approvalProcess = ApprovalProcess.APPROVAL_PROCESS_VENDASTA;
    request.billingProcess = BillingProcess.BILLING_PROCESS_VENDASTA_BILLING;

    return this.apiService.updateAppApprovalAndBillingSettings(request).pipe(map((resp) => resp.app));
  }

  public suspend(appId: string): Observable<HttpResponse<null>> {
    const request = new SuspendAppRequest();
    request.appId = appId;
    return this.apiService.suspendApp(request);
  }

  public unsuspend(appId: string): Observable<HttpResponse<null>> {
    const request = new UnsuspendAppRequest();
    request.appId = appId;
    return this.apiService.unsuspendApp(request);
  }

  public create(app: MarketplaceApp, partnerId: string, parentId?: string): Observable<AppResponse> {
    const request = this._buildCreateAppRequest(app, partnerId, parentId);
    return this.apiService.createApp(request).pipe(
      tap(() => {
        // This is to fix the eventual consistency with sql secondary index
        // Remove once we on longer use sql for getting partners apps
      }),
    );
  }

  _buildCreateAppRequest(app: MarketplaceApp, partnerId: string, parentId: string): CreateAppRequest {
    const request = new CreateAppRequest();
    if (app.integration) {
      request.accountModWebhookUrl = app.integration.accountModWebhookUrl;
      request.activationRequiresApproval = app.integration.activationRequiresApproval;
      request.addonPurchaseWebhookUrl = app.integration.addonPurchaseWebhookUrl;
      request.contactWebhookUrl = app.integration.contactWebhookUrl;
      request.fulfillmentEmail = app.integration.fulfillmentEmail;
      request.logoutUrl = app.integration.logoutWebhookUrl;
      request.purchaseWebhookUrl = app.integration.purchaseWebhookUrl;
      request.partnerSettingsWebhookUrl = app.integration.partnerSettingsWebhookUrl;
      request.userModWebhookUrl = app.integration.userModWebhookUrl;
      request.sessionUrl = app.integration.sessionUrl;
      request.entryUrl = app.integration.entryUrl;
      request.publicKey = app.integration.publicKey;
      request.allowWhiteLabeling = app.integration.allowWhiteLabeling;
    }
    request.multipleActivations = app.multipleActivations;
    request.allowPriceNegotiation = app.allowPriceNegotiation;
    request.billingFrequency = app.billingFrequency;
    request.currency = app.currency;
    request.endUserMarketing = app.endUserMarketing;
    request.headerImage = app.headerImage;
    request.icon = app.icon;
    request.lmiCategories = app.lmiCategories;
    request.name = app.name;
    request.deliveryMethod = app.deliveryMethod;
    request.orderForm = app.orderForm;
    request.price = app.price;
    request.suggestedRetailPrice = app.suggestedRetailPrice;
    request.preventPriceConversionBetweenCurrencies = app.preventPriceConversionBetweenCurrencies;
    request.resellerMarketing = app.resellerMarketing;

    request.restrictions = new Restrictions({});

    if (app.restrictions) {
      request.country = app.restrictions.country;
      request.restrictions.country = app.restrictions.country;
    }

    if (parentId) {
      request.restrictions.appRequirement = new AppRequirement({
        rules: [new Rule({ appIds: [parentId] })],
      });
    }

    request.screenshot = app.screenshot;
    request.tagline = app.tagline;
    request.usesOrderForm = app.usesOrderForm;
    request.usesActivationSpecificEntryUrl = app.usesActivationSpecificEntryUrl;
    request.usesResellerMarketing = app.usesResellerMarketing;
    request.usesIntegration = app.usesIntegration;
    request.commitment = app.commitment;
    request.usesSsoIntegration = app.usesSsoIntegration;
    request.discoverable = app.discoverable;
    request.applicationUrl = app.applicationUrl;

    if (app.appId) {
      request.customAppId = app.appId;
    }
    if (partnerId) {
      request.partnerId = partnerId;
    }
    request.distributionSetting = DistributionSetting.DISTRIBUTION_SETTING_SELF;

    return request;
  }

  public updateGeneralSettings(
    app: MarketplaceApp,
    suggestedRetailSetupFee: number,
    fieldMask?: FieldMask,
  ): Observable<MarketplaceApp> {
    if (!fieldMask) {
      fieldMask = new FieldMask({
        paths: ['Discoverable'],
      });
    }
    const request = MarketplaceAppService.createUpdateAppGeneralSettingsRequest(
      app,
      suggestedRetailSetupFee,
      fieldMask,
    );
    return this.apiService.updateAppGeneralSettings(request).pipe(map((resp: AppResponse) => resp.app));
  }

  public updateRequiredBusinessData(app: MarketplaceApp, fieldMask?: FieldMask): Observable<MarketplaceApp> {
    if (!fieldMask) {
      fieldMask = new FieldMask({
        paths: [
          'Restrictions.RequiredBusinessData.DescriptionShort',
          'Restrictions.RequiredBusinessData.DescriptionLong',
          'Restrictions.RequiredBusinessData.Hours',
          'Restrictions.RequiredBusinessData.Services',
          'Restrictions.RequiredBusinessData.Brands',
        ],
      });
    }
    const request = MarketplaceAppService.createUpdateAppGeneralSettingsRequest(app, 0, fieldMask);
    return this.apiService.updateAppGeneralSettings(request).pipe(map((resp: AppResponse) => resp.app));
  }

  public updateEndUserMarketingInformation(app: MarketplaceApp): Observable<MarketplaceApp> {
    const request = MarketplaceAppService.createUpdateAppMarketingInformationRequest(app.appId, app.endUserMarketing);
    return this.apiService.updateAppEndUserMarketingInformation(request).pipe(map((resp: AppResponse) => resp.app));
  }

  public updateResellerMarketingInformation(app: MarketplaceApp): Observable<MarketplaceApp> {
    const request = MarketplaceAppService.createUpdateAppMarketingInformationRequest(
      app.appId,
      app.resellerMarketing,
      app.usesResellerMarketing,
    );
    return this.apiService.updateAppResellerMarketingInformation(request).pipe(map((resp: AppResponse) => resp.app));
  }

  public updateSSOIntegration(app: MarketplaceApp): Observable<MarketplaceApp> {
    const request = MarketplaceAppService.createUpdateAppSSOUrlsRequest(app);
    return this.apiService.updateAppSsoIntegration(request).pipe(map((resp: AppResponse) => resp.app));
  }

  public updateIntegration(app: MarketplaceApp): Observable<MarketplaceApp> {
    const request = MarketplaceAppService.createUpdateAppIntegrationRequest(app);
    return this.apiService.updateAppIntegration(request).pipe(map((resp: AppResponse) => resp.app));
  }

  public updateOrderForm(app: MarketplaceApp): Observable<MarketplaceApp> {
    const request = MarketplaceAppService.createUpdateAppOrderFormRequest(
      app.appId,
      app.usesOrderForm,
      app.usesActivationSpecificEntryUrl,
      app.orderForm,
      app.applicationUrl,
    );
    return this.apiService.updateAppOrderForm(request).pipe(map((resp: AppResponse) => resp.app));
  }

  public deleteApp(appId: string): Observable<HttpResponse<null>> {
    const request = new DeleteAppRequest();
    request.appId = appId;
    return this.apiService.deleteApp(request);
  }

  public updateAppTrialConfiguration(app: MarketplaceApp): Observable<MarketplaceApp> {
    const request = new UpdateAppTrialConfigurationRequest();

    request.appId = app.appId;
    request.trialsEnabled = app.trialConfiguration.trialEnabled;
    request.duration = app.trialConfiguration.trialDuration;
    request.endOfTrialAction = EndOfTrialActions.DO_NOTHING;

    return this.apiService.updateAppTrialConfiguration(request).pipe(map((resp: AppResponse) => resp.app));
  }

  public isAppEnabled(appId: string): Observable<boolean> {
    return this.marketplacePackagesApiService
      .getMultiIsAppEnabled({ appIds: [appId] })
      .pipe(map((response) => response.appEnablement[0].enabled));
  }

  public getFulfillmentOrderConfig(appId: string): Observable<FulfillmentOrderConfig> {
    return this.fufillmentOrderConfigApiService
      .getFulfillmentOrderConfig({
        appId: appId,
        release: ReleaseType.RELEASE_TYPE_DRAFT,
        projectionFilter: {
          includeFormConfig: true,
        },
      })
      .pipe(map((response) => response.config));
  }
}
